import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ShieldCheck, 
  ArrowRight, 
  Layers, 
  Compass, 
  Globe, 
  HeartPulse, 
  Shield, 
  Car, 
  CheckCircle2, 
  X 
} from 'lucide-react';

export default function Products() {
  const location = useLocation();
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [formData, setFormData] = useState({ userName: '', userEmail: '', userPhone: '', capitalSize: 'Sum Insured: ₹10 Lakhs+' });
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (location.hash === '#insurance') {
      const element = document.getElementById('insurance');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  const products = [
    {
      title: "Mutual Funds (MF)",
      subtitle: "SIPs & Direct Plans",
      description: "Wealth accumulation through disciplined equity and debt SIPs managed by top fund managers.",
      link: "/products/mutual-funds",
      bgImage: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?auto=format&fit=crop&q=80&w=600",
      imgStyle: "object-cover object-center",
      badge: "Popular"
    },
    {
      title: "Specialized Investment Funds (SIF)",
      subtitle: "Focused Strategies",
      description: "High-conviction, tailored fund strategies designed for target market capitalizations and sectors.",
      link: "/products/sif",
      bgImage: "/sif.png",
      imgStyle: "object-cover object-center",
      badge: "High Growth"
    },
    {
      title: "Portfolio Management Services (PMS)",
      subtitle: "Managed Portfolios",
      description: "Professional, personalized equity portfolio management for high-net-worth wealth expansion.",
      link: "/products/pms",
      bgImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=600",
      imgStyle: "object-cover object-top",
      badge: "HNI Advisory"
    },
    {
      title: "Fixed Deposits (FD)",
      subtitle: "Stable, Insured Returns",
      description: "Guaranteed-return fixed income options backed by top banks and NBFCs for complete capital safety.",
      link: "/products/bonds-fds",
      bgImage: "https://images.unsplash.com/photo-1579621970795-87facc2f976d?auto=format&fit=crop&q=80&w=600",
      imgStyle: "object-cover object-center",
      badge: "Safe Income"
    },
    {
      title: "Alternate Investment Funds (AIF)",
      subtitle: "Category I, II & III",
      description: "Exclusive hedge funds, private equity, and real estate funds for institutional-grade diversification.",
      link: "/products/aif",
      bgImage: "https://images.unsplash.com/photo-1610375461246-83df859d849d?auto=format&fit=crop&q=80&w=600",
      imgStyle: "object-cover object-center",
      badge: "Alternative"
    },
    {
      title: "International / Offshore Platforms",
      subtitle: "Global Diversification",
      description: "Invest in US & global equity markets to hedge against rupee depreciation and geographic risk.",
      link: "/products/offshore",
      bgImage: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?auto=format&fit=crop&q=80&w=600",
      imgStyle: "object-cover object-center",
      badge: "Global Equity"
    },
    {
      title: "Pre-IPO Unlisted Opportunities",
      subtitle: "Early-Stage Access",
      description: "Early entry into high-growth unlisted companies and unicorns before they list on public exchanges.",
      link: "/products/pre-ipo",
      bgImage: "/pre-ipo.png",
      imgStyle: "object-cover object-center",
      badge: "Pre-Listing"
    },
    {
      title: "Bonds / NCDs",
      subtitle: "Fixed-Income Ladder",
      description: "Corporate bonds, NCDs, and sovereign gold bonds offering predictable regular interest yields.",
      link: "/products/bonds-fds",
      bgImage: "https://images.unsplash.com/photo-1450133064473-71024230f91b?auto=format&fit=crop&q=80&w=600",
      imgStyle: "object-cover object-center",
      badge: "Fixed Yield"
    }
  ];

  // Insurance Services Data
  const insuranceServices = [
    {
      id: "health",
      title: "HEALTH INSURANCE",
      subtitle: "Medical & Critical Illness Coverage",
      description: "Comprehensive health coverage protecting your wealth from unexpected medical emergencies with cash-less hospital access.",
      icon: <HeartPulse className="w-7 h-7 text-[#16325B]" />,
      bgImage: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&q=80&w=600",
      features: ["Cashless Hospitalization", "Critical Illness Cover", "Tax Savings under Sec 80D", "No Claim Bonus Protection"]
    },
    {
      id: "term-life",
      title: "TERM LIFE INSURANCE",
      subtitle: "Pure Protection & Family Income Security",
      description: "High-cover life protection ensuring your family's financial independence and goal fulfillment even in your absence.",
      icon: <Shield className="w-7 h-7 text-[#16325B]" />,
      bgImage: "https://images.unsplash.com/photo-1450133064473-71024230f91b?auto=format&fit=crop&q=80&w=600",
      features: ["High Sum Assured at Low Premium", "Lumpsum + Monthly Income Payout", "Tax Benefits under Sec 80C", "Terminal Illness Rider Included"]
    },
    {
      id: "general",
      title: "GENERAL INSURANCE",
      subtitle: "Motor, Property & Commercial Risk Protection",
      description: "Complete asset protection covering vehicles, residential properties, commercial assets, and travel uncertainties.",
      icon: <Car className="w-7 h-7 text-[#16325B]" />,
      bgImage: "https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&q=80&w=600",
      features: ["Comprehensive Motor Policy", "Property & Asset Cover", "International Travel Protection", "Fast Claim Settlement Assistance"]
    }
  ];

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMsg('');

    try {
      const response = await fetch('http://localhost:5000/api/enroll', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userName: formData.userName,
          userEmail: formData.userEmail,
          userPhone: formData.userPhone,
          productName: selectedInsurance.title,
          capitalSize: formData.capitalSize
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMsg(`Successfully enrolled for ${selectedInsurance.title}! Our advisor will contact you shortly.`);
        setFormData({ userName: '', userEmail: '', userPhone: '', capitalSize: 'Sum Insured: ₹10 Lakhs+' });
      } else {
        alert(data.message || 'Enrollment failed.');
      }
    } catch (err) {
      console.error(err);
      alert('Server Connection Error. Make sure backend is running.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#FAF8F5] text-[#0B2540] min-h-screen font-sans selection:bg-[#16325B] selection:text-white pb-20">
      
      {/* 1. Page Header Banner */}
      <section className="bg-[#0B2239] text-white pt-16 pb-20 px-6 relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center space-y-4 relative z-10">
          <div className="inline-flex items-center gap-2 bg-[#16325B]/40 border border-[#3B82F6]/30 text-[#60A5FA] text-xs font-semibold px-4 py-1.5 rounded-full">
            <Layers className="w-4 h-4" />
            Comprehensive Asset Ecosystem
          </div>

          <h1 className="font-serif-title text-4xl sm:text-6xl font-extrabold tracking-tight">
            Invest in <span className="text-[#60A5FA] italic font-normal">everything</span> that matters
          </h1>

          <p className="text-gray-300 text-xs sm:text-sm max-w-2xl mx-auto font-normal leading-relaxed">
            From your first mutual fund SIP to pre-IPO opportunities — a single, institutional-grade relationship across every asset class.
          </p>
        </div>
      </section>

      {/* 2. Investments Products Grid */}
      <section className="py-16 px-6 max-w-7xl mx-auto -mt-8 relative z-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((item, index) => (
            <Link
              key={index}
              to={item.link}
              className="group bg-white rounded-3xl overflow-hidden border border-gray-200/80 shadow-sm hover:shadow-2xl hover:border-[#16325B]/40 transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                <div className="h-44 w-full overflow-hidden bg-gray-100 relative">
                  <img 
                    src={item.bgImage} 
                    alt={item.title} 
                    className={`w-full h-full ${item.imgStyle} group-hover:scale-105 transition-transform duration-500`}
                  />
                  <div className="absolute top-3 right-3 bg-[#0B2540]/80 backdrop-blur-md text-[#60A5FA] font-bold text-[10px] uppercase px-3 py-1 rounded-full border border-white/20">
                    {item.badge}
                  </div>
                </div>

                <div className="p-6 space-y-2">
                  <h3 className="font-serif-title text-lg font-bold text-[#0B2540] group-hover:text-[#16325B] transition-colors leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-xs text-[#16325B] font-bold">
                    {item.subtitle}
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed pt-1">
                    {item.description}
                  </p>
                </div>
              </div>

              <div className="px-6 pb-6 pt-2 flex items-center justify-between border-t border-gray-100/80 mt-2">
                <span className="text-[11px] font-semibold text-gray-400 group-hover:text-[#0B2540] transition">
                  Explore Structure
                </span>
                <div className="inline-flex items-center gap-1.5 text-[#16325B] font-bold text-xs group-hover:translate-x-1 transition-transform">
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>

            </Link>
          ))}
        </div>
      </section>

      {/* ========================================================= */}
      {/* 3. SECTION: INSURANCE & RISK PROTECTION SERVICES          */}
      {/* ========================================================= */}
      <section id="insurance" className="py-16 px-6 max-w-7xl mx-auto border-t border-gray-200/80 scroll-mt-24">
        <div className="text-center space-y-3 mb-12">
          <div className="inline-flex items-center gap-2 bg-[#EEF4FA] text-[#16325B] text-xs font-semibold px-4 py-1.5 rounded-full border border-[#C7D7E8]">
            <ShieldCheck className="w-4 h-4 text-[#16325B]" />
            IRDAI Registered Advisory Services
          </div>

          <h2 className="font-serif-title text-3xl sm:text-5xl font-extrabold text-[#0B2540] tracking-tight">
            Insurance & Risk <span className="text-[#16325B] italic font-normal">Protection</span>
          </h2>

          <p className="text-gray-600 text-xs sm:text-sm max-w-xl mx-auto leading-relaxed">
            Wealth creation is incomplete without capital protection. Secure your health, family, and assets with structured insurance solutions.
          </p>
        </div>

        {/* 3 Cards Insurance Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {insuranceServices.map((service) => (
            <div 
              key={service.id}
              className="bg-white rounded-3xl overflow-hidden border border-gray-200/80 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                {/* Header Image */}
                <div className="h-48 w-full relative overflow-hidden bg-slate-100">
                  <img 
                    src={service.bgImage} 
                    alt={service.title} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B2540]/90 via-[#0B2540]/40 to-transparent" />
                  
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur p-2.5 rounded-2xl shadow-md border border-gray-100">
                    {service.icon}
                  </div>

                  <div className="absolute bottom-4 left-6 right-6 text-white space-y-0.5">
                    <span className="text-[10px] font-bold text-[#60A5FA] uppercase tracking-wider block">
                      Insurance Category
                    </span>
                    <h3 className="font-serif-title text-xl font-bold tracking-tight">
                      {service.title}
                    </h3>
                  </div>
                </div>

                {/* Body Content */}
                <div className="p-6 space-y-4">
                  <p className="text-xs font-semibold text-[#16325B]">
                    {service.subtitle}
                  </p>
                  
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {service.description}
                  </p>

                  <div className="space-y-2 pt-2 border-t border-gray-100">
                    {service.features.map((feat, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-xs text-gray-700 font-medium">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#16325B] shrink-0" />
                        <span>{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Enroll Now CTA Button */}
              <div className="p-6 pt-0">
                <button
                  onClick={() => {
                    setSelectedInsurance(service);
                    setSuccessMsg('');
                  }}
                  className="w-full bg-[#16325B] hover:bg-[#0F213D] text-white font-bold py-3.5 px-6 rounded-2xl text-xs transition duration-300 shadow-md flex items-center justify-center gap-2"
                >
                  <span>Enroll Now</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 4. Bottom Trust & Transparency Banner */}
      <section className="px-6 max-w-7xl mx-auto pt-8">
        <div className="bg-white rounded-3xl border border-gray-200/80 p-8 sm:p-12 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-2xl bg-[#EEF4FA] text-[#16325B] flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div className="space-y-1">
              <h4 className="font-serif-title font-bold text-base text-[#0B2540]">Zero Direct Holding</h4>
              <p className="text-xs text-gray-500 leading-relaxed">Your money is processed directly with SEBI/AMFI registered entities and custodians.</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-2xl bg-[#EEF4FA] text-[#16325B] flex items-center justify-center shrink-0">
              <Compass className="w-5 h-5" />
            </div>
            <div className="space-y-1">
              <h4 className="font-serif-title font-bold text-base text-[#0B2540]">Goal Alignment</h4>
              <p className="text-xs text-gray-500 leading-relaxed">Every recommendation is mapped to your specific risk capacity and timeline.</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-2xl bg-[#EEF4FA] text-[#16325B] flex items-center justify-center shrink-0">
              <Globe className="w-5 h-5" />
            </div>
            <div className="space-y-1">
              <h4 className="font-serif-title font-bold text-base text-[#0B2540]">Unbiased Research</h4>
              <p className="text-xs text-gray-500 leading-relaxed">Independent evaluation of fund managers, debt ratings, and unlisted entities.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================= */}
      {/* ENROLL NOW MODAL FORM FOR INSURANCE                       */}
      {/* ========================================================= */}
      {selectedInsurance && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-8 shadow-2xl relative border border-gray-100">
            
            <button
              onClick={() => setSelectedInsurance(null)}
              className="absolute top-5 right-5 bg-gray-100 hover:bg-gray-200 text-gray-600 p-2 rounded-full transition"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-2 mb-6">
              <span className="text-[10px] font-bold text-[#16325B] uppercase tracking-widest bg-[#EEF4FA] px-3 py-1 rounded-full inline-block">
                Service Enrollment
              </span>
              <h3 className="font-serif-title text-2xl font-bold text-[#0B2540]">
                Enroll for {selectedInsurance.title}
              </h3>
              <p className="text-xs text-gray-500">
                Fill in your details for a personalized risk quote and coverage analysis from Haritika.
              </p>
            </div>

            {successMsg ? (
              <div className="text-center py-6 space-y-4">
                <div className="w-12 h-12 bg-[#EEF4FA] text-[#16325B] rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <p className="text-xs font-semibold text-[#0B2540]">{successMsg}</p>
                <button
                  onClick={() => setSelectedInsurance(null)}
                  className="bg-[#16325B] text-white px-6 py-2.5 rounded-full text-xs font-bold hover:bg-[#0F213D] transition"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleEnrollSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-[#0B2540] mb-1">Full Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Manish Kumar"
                    value={formData.userName}
                    onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                    className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl p-3 text-xs sm:text-sm focus:outline-none focus:border-[#16325B]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#0B2540] mb-1">Email Address *</label>
                  <input
                    type="email"
                    required
                    placeholder="you@email.com"
                    value={formData.userEmail}
                    onChange={(e) => setFormData({ ...formData, userEmail: e.target.value })}
                    className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl p-3 text-xs sm:text-sm focus:outline-none focus:border-[#16325B]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#0B2540] mb-1">Phone Number (WhatsApp) *</label>
                  <input
                    type="tel"
                    required
                    placeholder="+91 98765 43210"
                    value={formData.userPhone}
                    onChange={(e) => setFormData({ ...formData, userPhone: e.target.value })}
                    className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl p-3 text-xs sm:text-sm focus:outline-none focus:border-[#16325B]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#0B2540] mb-1">Preferred Coverage Amount</label>
                  <select
                    value={formData.capitalSize}
                    onChange={(e) => setFormData({ ...formData, capitalSize: e.target.value })}
                    className="w-full bg-[#FAF8F5] border border-gray-200 rounded-xl p-3 text-xs sm:text-sm focus:outline-none focus:border-[#16325B]"
                  >
                    <option value="Sum Insured: ₹5 Lakhs - ₹10 Lakhs">Sum Insured: ₹5 Lakhs - ₹10 Lakhs</option>
                    <option value="Sum Insured: ₹10 Lakhs - ₹25 Lakhs">Sum Insured: ₹10 Lakhs - ₹25 Lakhs</option>
                    <option value="Sum Insured: ₹50 Lakhs - ₹1 Crore">Sum Insured: ₹50 Lakhs - ₹1 Crore</option>
                    <option value="Sum Insured: ₹1 Crore+">Sum Insured: ₹1 Crore+</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#16325B] hover:bg-[#0F213D] text-white font-bold py-3.5 rounded-xl text-xs sm:text-sm transition duration-300 shadow-md disabled:opacity-50 mt-2"
                >
                  {loading ? 'Submitting Enrollment...' : 'Submit Service Enrollment'}
                </button>
              </form>
            )}

          </div>
        </div>
      )}

    </div>
  );
}